import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import { ICellRendererParams, IRowNode } from 'ag-grid-community'
import store from '../../../../../../../../store'
import { pushFunctionLayer } from '../../../../../../../../store/functionLayer'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import { getOpenWbsItemDetailSpec } from '../../../../../../../../lib/functions/wbsItem'
import {
  FONT_FAMILY,
  FontSize,
} from '../../../../../../../../styles/commonStyles'
import validator from '../../../../../../meta/validator'
import { inputErrorColor } from '../../../../lib/validator'
import { getPathByExternalId } from '../../../../../../../pages'
import { WbsItemIcon } from '../../../../../../../components/icons/WbsItemIcon'
import { measureTextWidthPx } from '../../../../../../../commons/textWidth'
import { FloatingEditableIcon } from '../../common/text'
import { setError } from '../../../../../../BulkSheetView/lib/validation'

export interface Props extends ICellRendererParams {
  onCloseWbsItemDetail?: (wbsItem) => void
  beforeOpenWbsItemDetail?: (uuid: string) => Promise<boolean | undefined>
  uiMeta?: Partial<FunctionProperty>
}

const NotificationDot = () => (
  <p className="sevend-ag-cell-project-plan-tree__annotation-has-data" />
)

export const WbsItemCellRenderer = memo(
  (props: Props) => {
    const {
      value,
      data,
      uiMeta,
      onCloseWbsItemDetail,
      beforeOpenWbsItemDetail,
      node,
    } = props
    const ref = useRef<HTMLDivElement | null>(null)
    const [tooltip, setTooltip] = useState('')
    const isValid = useRef<boolean>(true)

    useEffect(() => {
      const err = uiMeta
        ? validator
            .validate(value, data, uiMeta as FunctionProperty, () => undefined)
            ?.getMessage()
        : undefined
      setError(data, err, props)
      setTooltip(err ?? '')
      isValid.current = !err
    }, [value])

    const openDetail = useCallback(
      async (node: IRowNode, event: any) => {
        const d = node.data
        const w = d?.body?.wbsItem ?? d?.wbsItem
        if (!w) return
        if (d.added || d.edited) {
          const success = await beforeOpenWbsItemDetail?.(d.uuid)
          if (!success) return
        }
        const openDetailSpec = await getOpenWbsItemDetailSpec(true, w)
        const layer = openDetailSpec.layer
        openDetailSpec.onOpen && openDetailSpec.onOpen()
        if (
          !openDetailSpec.openInDialog ||
          (event && (event['ctrlKey'] || event['metaKey']))
        ) {
          window.open(`${getPathByExternalId(layer.externalId)}/${layer.code!}`)
        } else {
          store.dispatch(
            pushFunctionLayer({
              ...layer,
              onClose: async () => {
                onCloseWbsItemDetail?.(w)
              },
            })
          )
        }
      },
      [beforeOpenWbsItemDetail]
    )

    useEffect(() => {
      if (!isValid.current) return
      // requestAnimationFrameでDOM計測を遅延させ、Forced Reflowを回避
      const frameId = requestAnimationFrame(() => {
        const offsetWidth = ref.current?.offsetWidth
        if (value && offsetWidth) {
          const textWidth = measureTextWidthPx(
            value,
            `${FontSize.MEDIUM}px ${FONT_FAMILY}`
          )
          if (offsetWidth - textWidth < 0) {
            setTooltip(value)
          }
        }
      })
      return () => cancelAnimationFrame(frameId)
    }, [value])

    const wbsItem = data.body?.wbsItem ?? data.wbsItem
    const type = wbsItem?.wbsItemType
    if (!type) return <></>
    const description = wbsItem.description

    return (
      <Tooltip
        title={tooltip}
        placement={'bottom-start'}
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: `${FontSize.MEDIUM}px`,
              marginTop: '5px !important',
            },
          },
        }}
      >
        <div className="sevend-ag-cell-icon-text__icon-and-text" ref={ref}>
          <div className="sevend-ag-cell-icon-text__icon-field">
            <button
              className="sevend-ag-cell-icon-text__icon--button"
              onClick={e => openDetail(node, e)}
            >
              <WbsItemIcon type={type} />
            </button>
            {description && <NotificationDot />}
          </div>
          <div
            className="sevend-ag-cell-icon-text__text-field"
            style={{
              backgroundColor:
                value && isValid.current ? 'transparent' : inputErrorColor,
            }}
          >
            {value}
          </div>
          <FloatingEditableIcon {...props} />
        </div>
      </Tooltip>
    )
  },
  // カスタム比較関数: 描画に影響するpropsのみを比較
  (prevProps, nextProps) => {
    // valueが変わったら再レンダリング
    if (prevProps.value !== nextProps.value) return false

    // wbsItemの参照を取得
    const prevWbsItem = prevProps.data?.body?.wbsItem ?? prevProps.data?.wbsItem
    const nextWbsItem = nextProps.data?.body?.wbsItem ?? nextProps.data?.wbsItem

    // wbsItemが両方nullish
    if (!prevWbsItem && !nextWbsItem) return true
    // 片方だけnullish
    if (!prevWbsItem || !nextWbsItem) return false

    // wbsItemの描画に関係するプロパティを比較
    if (prevWbsItem.wbsItemType !== nextWbsItem.wbsItemType) return false
    if (prevWbsItem.description !== nextWbsItem.description) return false

    return true
  }
)
